import { TaskItem } from "../../types/topic";

/**
 * Task data as stored in the database
 * This interface extends the existing TaskItem interface
 * but adds optional metadata for offline support
 */
export interface TaskData extends TaskItem {
  metadata?: {
    client_id?: string;
    created_at?: string;
    updated_at?: string;
    [key: string]: any;
  };
  isDeleted?: boolean; // Used for offline tracking only
}

/**
 * Formatted task with additional derived properties
 * and more consistent naming for React Query use
 */
export interface FormattedTask {
  id: string;
  topicId: string;
  userId: string;
  title: string;
  description: string | null;
  status: string;
  position: number;
  deadline: string | null;
  tags: string[] | null;
  isHighlighted: boolean;
  assignedTo: string | null;
  assigneeName: string | null;
  createdAt: string;
  updatedAt: string | null;
  // Additional UI properties can be added here as needed
  // We can add more UI-specific properties here as needed
}

/**
 * Input for creating a new task
 */
export interface CreateTaskInput {
  topicId: string;
  title: string;
  description?: string;
  status: string;
  tags?: string[];
  deadline?: string | null;
  assignedTo?: string | null;
  isHighlighted?: boolean;
}

/**
 * Input for updating a task
 */
export interface UpdateTaskInput {
  title?: string;
  description?: string | null;
  status?: string;
  tags?: string[] | null;
  deadline?: string | null;
  assignedTo?: string | null;
  isHighlighted?: boolean;
}

/**
 * Input for reordering a task
 */
export interface ReorderTaskInput {
  topicId: string;
  taskId: string;
  newStatus?: string;
  newIndex: number;
}

/**
 * Input for batch updating multiple tasks
 */
export interface BatchUpdateTasksInput {
  topicId: string;
  taskIds: string[];
  updates: Partial<UpdateTaskInput>;
}

/**
 * Helper function to format a task from database format to UI format
 * @param task The raw task data from the database
 * @returns A formatted task with derived properties for UI
 */
export function formatTask(task: TaskData): FormattedTask {
  return {
    id: task.id,
    topicId: task.topic_id,
    userId: task.user_id,
    title: task.title,
    description: task.description || null,
    status: task.status,
    position: task.position || 0,
    deadline: task.deadline || null,
    tags: task.tags || null,
    isHighlighted: task.is_highlighted || false,
    assignedTo: task.assigned_to || null,
    assigneeName: task.assignee_name || null,
    createdAt: task.created_at,
    updatedAt: task.updated_at || null,
    // Add any other derived UI properties here
  };
}

/**
 * Converts a FormattedTask back to TaskData for database operations
 * @param task The formatted task with UI-specific properties
 * @returns Raw task data for database operations
 */
export function formatTaskForDatabase(task: FormattedTask): TaskData {
  return {
    id: task.id,
    topic_id: task.topicId,
    user_id: task.userId,
    title: task.title,
    description: task.description,
    status: task.status,
    position: task.position,
    deadline: task.deadline,
    tags: task.tags,
    is_highlighted: task.isHighlighted,
    assigned_to: task.assignedTo,
    assignee_name: task.assigneeName,
    created_at: task.createdAt,
    updated_at: task.updatedAt || undefined,
  };
}